import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';
import { Button, Radio, Segmented, Select, Switch, TreeSelect, message } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import { useStore } from '../../store/store';
import { useOrganizationalCharts } from '../../hooks';
import { ItemTypes } from '../../constants/constants';
import { NavbarButton } from '../Layout/NavbarButton';
import NavbarSelector from '../Layout/NavbarSelector';
import LabeledControlRow from '../common/LabeledControlRow';
import FieldGroupLabel from '../common/boxStyleFields/FieldGroupLabel';
import BoxFormControlsPortal from '../RightSide/BoxFormControlsPortal';
import { iconButtonStyle } from '../../utils/styles';
import {
  createBlankOrganizationalChart,
  createTemplateOrganizationalChart,
  getEntities,
  getOrgChartTagsWithCoverage,
} from '../../requests/organizationalCharts';
import {
  ORG_CHART_MODES,
  isAgencyOrgChartMode,
  orgChartMode,
  orgChartModeChange,
} from '../../constants/orgChart';
import { buildEntityTree } from './entityTree';

// Valeur du radio qui regroupe les trois modes « organigramme d'une agence ».
// Pas un mode en soi : la box garde entity / parent_entity / agency_tag, choisi
// ensuite au Segmented.
const AGENCY_RADIO = 'agency';

const hintStyle = { fontSize: 11, lineHeight: 1.4, color: 'rgba(11, 34, 57, 0.65)' };
const warningStyle = { ...hintStyle, color: '#a67c1f', display: 'flex', gap: 6 };

// Le repli du resolver, montré au lieu d'être raconté : la phrase grise qui le
// décrivait tenait sur trois lignes et se relisait à chaque réglage. L'étape
// « étiquette » n'apparaît que si une étiquette est choisie — sans elle, la
// cascade commence vraiment à l'organigramme par défaut.
const OrgChartCascade = ({ tagName }) => {
  const { t } = useTranslation();
  const steps = [
    ...(tagName ? [t('org_chart_cascade_tag', { name: tagName })] : []),
    t('org_chart_cascade_default'),
    t('org_chart_cascade_manual'),
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {steps.map((step, index) => (
        <React.Fragment key={step}>
          {index > 0 && (
            <span style={{ ...hintStyle, fontSize: 10, opacity: 0.8 }}>
              {`↓ ${t('org_chart_cascade_else')}`}
            </span>
          )}
          <span style={{ ...hintStyle, fontWeight: index === 0 ? 600 : 400 }}>{step}</span>
        </React.Fragment>
      ))}
    </div>
  );
};

// Une étiquette qu'aucun organigramme de l'agence visée ne porte retombe
// silencieusement sur l'organigramme par défaut (cf. Resolver) : le compteur de
// couverture le dit avant l'injection plutôt qu'après.
const TagCoverageOption = ({ name, entitiesCount, t }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center' }}>
    <span style={{ opacity: entitiesCount ? 1 : 0.45 }}>{name}</span>
    <span style={{ fontSize: 10, whiteSpace: 'nowrap', color: entitiesCount ? 'rgba(11, 34, 57, 0.55)' : '#a67c1f' }}>
      {entitiesCount
        ? t('org_chart_tag_coverage', { count: entitiesCount })
        : t('org_chart_tag_coverage_none')}
    </span>
  </div>
);

const selector = ({ currentDocument, currentProposal, updateBox, boxes }) => ({
  currentDocument,
  currentProposal,
  updateBox,
  boxes,
});

// Contrôles de la box organigramme, dans l'inspecteur de droite (isSelected, cf.
// OrgChart.jsx) : la box peut être resizée très étroite (partage de ligne comme
// une img), elle n'a pas la place d'héberger un sélecteur. Le panel de droite
// est l'emplacement des réglages de box (comme Image/Table), contrairement à la
// navbar où le sélecteur flottait loin de la box concernée.
// `hasChart` bascule entre le picker (aucun chart choisi) et les actions
// d'édition (chart déjà choisi) — passées par OrgChart.jsx (onEdit/onChangeChart/onSync)
// pour rester la seule source de vérité du verrou/de la modal.
const OrgChartPanel = ({ box, hasChart, frozen, isCopy, lockedByOther, lockHolder, onEdit, onChangeChart, onSync, isTemplate }) => {
  const { t } = useTranslation();
  const { currentDocument, currentProposal, updateBox, boxes } = useStore(selector);
  const proposalId = currentProposal?.id;
  const mode = orgChartMode(box);
  const agencyMode = isTemplate && isAgencyOrgChartMode(mode);
  const pickedAgencyMode = isTemplate && mode === ORG_CHART_MODES.AGENCY_TAG;

  const { data: charts = [], isLoading } = useOrganizationalCharts(
    { proposalId, documentType: currentDocument.type },
    { enabled: !hasChart && !!proposalId && currentDocument.type !== 'template' }
  );

  // Réglages « organigramme d'une agence » : requêtes lancées seulement quand ils
  // sont affichés (hooks appelés inconditionnellement, le réseau ne l'est pas).
  // Toutes les agences d'un coup — quelques dizaines par société — pour que
  // l'arbre se filtre côté client.
  const { data: entities, isLoading: entitiesLoading } = useQuery('OrgChartEntities', getEntities, {
    enabled: pickedAgencyMode,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
  const entityTree = useMemo(() => buildEntityTree(entities), [entities]);
  // Relu au retour sur l'onglet : les étiquettes se créent dans l'admin web, souvent
  // dans un autre onglet pendant que le modèle reste ouvert — et leur couverture
  // change dès qu'un organigramme y est rattaché.
  const { data: tagCoverage } = useQuery('OrgChartTagsCoverage', getOrgChartTagsWithCoverage, {
    enabled: agencyMode,
  });
  const tags = tagCoverage?.tags ?? [];
  const entitiesWithCharts = tagCoverage?.entities_with_charts_count ?? 0;
  const selectedTag = tags.find((tag) => tag.id === box.orgChartTargetTagId) ?? null;
  // Agences qui n'ont rien sous cette étiquette : elles prendront leur organigramme
  // par défaut. Muet quand la couverture est totale — l'avertissement n'a rien à dire.
  const uncoveredEntities = selectedTag
    ? Math.max(entitiesWithCharts - selectedTag.entities_count, 0)
    : 0;

  // Un organigramme ne peut apparaître qu'une fois par proposal (évite copies partagées).
  const usedSourceIds = new Set(
    (boxes || [])
      .filter((b) => b.type === ItemTypes.ORG_CHART && b.id !== box.id && b.orgChartSourceId)
      .map((b) => b.orgChartSourceId)
  );
  const availableCharts = charts.filter((c) => !usedSourceIds.has(c.id));
  const multipleEntities = new Set(availableCharts.map((c) => c.entity_id)).size > 1;

  const handleSelect = (id) => {
    const chart = charts.find((c) => c.id === id);
    updateBox(box.id, (b) => {
      b.orgChartId = id;
      b.orgChartSourceId = id;
      // Choix explicite : il l'emporte sur ce que le modèle avait prévu, et la
      // résolution automatique ne repassera pas derrière (cf. constants/orgChart).
      b.orgChartMode = ORG_CHART_MODES.CHOOSE;
      b.content = { imageUrl: chart?.image_url || null, name: chart?.name || null, ratio: null };
    });
  };

  const { mutate: createBlank, isLoading: creatingBlank } = useMutation(
    () => createBlankOrganizationalChart(proposalId),
    {
      onSuccess: (chart) => {
        updateBox(box.id, (b) => {
          // Même drapeau que la création automatique : sans lui, une box en mode
          // vierge s'en referait un à chaque ouverture du document.
          b.orgChartResolved = true;
          b.orgChartId = chart.id;
          b.orgChartSourceId = null;
          b.content = { imageUrl: chart.image_url || null, name: chart.name || null, ratio: null };
        });
      },
      onError: () => message.error(t('org_chart_create_error')),
    }
  );

  // Organigramme composé dans le modèle : créé à la demande, puis ouvert dans
  // l'éditeur. La box en garde la source pour que la proposition sache d'où
  // partir (copie si le modèle l'autorise, cf. OrgChart.jsx).
  const { mutate: createForTemplate, isLoading: composing } = useMutation(
    () => createTemplateOrganizationalChart(currentDocument.id),
    {
      onSuccess: (chart) => {
        updateBox(box.id, (b) => {
          b.orgChartId = chart.id;
          b.orgChartSourceId = chart.id;
          b.content = { imageUrl: chart.image_url || null, name: chart.name || null, ratio: null };
        });
        onEdit?.();
      },
      onError: () => message.error(t('org_chart_create_error')),
    }
  );

  const composeTemplateChart = () => (box.orgChartId ? onEdit?.() : createForTemplate());

  // Modèle : aucun organigramme à choisir ici (le contenu n'existe qu'une fois
  // le document injecté dans un proposal), sauf en mode « organigramme de ce
  // modèle » où c'est justement le modèle qui le porte. Le réglage est donc le
  // comportement à l'injection, avec la phrase qui l'explique — illisible dans
  // une box étroite, à l'aise dans le panel.
  if (isTemplate) {
    const changeMode = (nextMode) =>
      updateBox(box.id, (b) => Object.assign(b, orgChartModeChange(b, nextMode)));

    return (
      <BoxFormControlsPortal title={t('org_chart')} layout='column'>
        <Radio.Group
          value={agencyMode ? AGENCY_RADIO : mode}
          onChange={(e) => {
            const { value } = e.target;
            if (value !== AGENCY_RADIO) changeMode(value);
            // Entrée dans le groupe : l'agence de la proposition, réglage le plus courant.
            else if (!agencyMode) changeMode(ORG_CHART_MODES.ENTITY);
          }}
          style={{ display: 'flex', flexDirection: 'column', gap: 6 }}
        >
          <Radio value={ORG_CHART_MODES.CHOOSE}>{t('org_chart_mode_choose')}</Radio>
          <Radio value={ORG_CHART_MODES.BLANK}>{t('org_chart_mode_blank')}</Radio>
          <Radio value={ORG_CHART_MODES.TEMPLATE}>{t('org_chart_mode_template')}</Radio>
          {/* En dernier : ses réglages s'affichent juste en dessous. */}
          <Radio value={AGENCY_RADIO}>{t('org_chart_mode_agency')}</Radio>
        </Radio.Group>
        {agencyMode && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {/* Deux questions nommées plutôt que quatre contrôles anonymes : le
                Segmented n'avait pas de sujet (« Concernée » quoi ?) et le Select
                d'étiquette, posé sous celui des agences, se lisait comme sa suite. */}
            <FieldGroupLabel>{t('org_chart_agency_group')}</FieldGroupLabel>
            {/* Pas de `block` : des parts égales tronquaient « Concernée » dans le
                panneau étroit ; chaque segment prend la largeur de son libellé. */}
            <Segmented
              size='small'
              value={mode}
              onChange={changeMode}
              options={[
                { value: ORG_CHART_MODES.ENTITY, label: t('org_chart_agency_scope_entity') },
                { value: ORG_CHART_MODES.PARENT_ENTITY, label: t('org_chart_agency_scope_parent_entity') },
                { value: ORG_CHART_MODES.AGENCY_TAG, label: t('org_chart_agency_scope_agency_tag') },
              ]}
            />
            {pickedAgencyMode && (
              <TreeSelect
                size='small'
                showSearch
                allowClear
                treeDefaultExpandAll
                treeNodeFilterProp='title'
                style={{ width: '100%' }}
                // Le panneau est étroit : la liste déroulante s'élargit pour que
                // l'indentation de l'arbre ne tronque pas les noms d'agence.
                popupMatchSelectWidth={280}
                listHeight={320}
                loading={entitiesLoading}
                placeholder={t('org_chart_agency_placeholder')}
                notFoundContent={t('org_chart_agency_not_found')}
                value={box.orgChartTargetEntityId ?? undefined}
                onChange={(entityId) =>
                  updateBox(box.id, (b) => { b.orgChartTargetEntityId = entityId ?? null; })
                }
                treeData={entityTree}
              />
            )}
            <div style={hintStyle}>{t(`org_chart_agency_scope_hint_${mode}`)}</div>

            <FieldGroupLabel>{t('org_chart_tag_group')}</FieldGroupLabel>
            <div style={hintStyle}>{t('org_chart_tag_group_hint')}</div>
            <Select
              size='small'
              allowClear
              showSearch
              placeholder={t('org_chart_tag_placeholder')}
              value={box.orgChartTargetTagId ?? undefined}
              onChange={(tagId) =>
                updateBox(box.id, (b) => { b.orgChartTargetTagId = tagId ?? null; })
              }
              options={tags.map((tag) => ({
                value: tag.id,
                label: tag.name,
                entitiesCount: tag.entities_count ?? 0,
              }))}
              // `label` reste le nom seul : c'est lui qui s'affiche une fois le
              // champ replié, où le compteur n'aurait pas la place. Il ne paraît
              // que dans la liste déroulante, qui s'élargit.
              optionRender={({ data }) => (
                <TagCoverageOption name={data.label} entitiesCount={data.entitiesCount} t={t} />
              )}
              popupMatchSelectWidth={260}
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
            />
            {!!uncoveredEntities && (
              <div style={warningStyle}>
                <FontAwesomeIcon icon={icon({ name: 'triangle-exclamation', style: 'light' })} />
                <span>
                  {t('org_chart_tag_coverage_warning', {
                    covered: selectedTag.entities_count,
                    total: entitiesWithCharts,
                    name: selectedTag.name,
                  })}
                </span>
              </div>
            )}
          </div>
        )}
        {/* Modes d'agence : la cascade de repli remplace la phrase, qui la
            racontait en trois lignes. Les autres modes gardent leur phrase —
            ils n'ont pas de repli à montrer. */}
        {agencyMode ? (
          <OrgChartCascade tagName={selectedTag?.name} />
        ) : (
          <div style={hintStyle}>{t(`org_chart_mode_hint_${mode}`)}</div>
        )}
        {mode === ORG_CHART_MODES.TEMPLATE && (
          <>
            <Button
              size='small'
              loading={composing}
              icon={<FontAwesomeIcon icon={icon({ name: 'pen', style: 'light' })} />}
              onClick={composeTemplateChart}
              style={{ width: '100%' }}
            >
              {box.orgChartId ? t('org_chart_template_edit') : t('org_chart_template_compose')}
            </Button>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <Switch
                size='small'
                // Comme les toggles « Affichage » : sans ça le mousedown vole le
                // focus et fait remonter la sélection au container.
                onMouseDown={(e) => e.preventDefault()}
                checked={!!box.orgChartUnlocked}
                onChange={(checked) => updateBox(box.id, (b) => { b.orgChartUnlocked = checked; })}
              />
              <span style={{ fontWeight: 500 }}>{t('org_chart_template_unlock')}</span>
            </div>
          </>
        )}
      </BoxFormControlsPortal>
    );
  }

  // Organigramme du modèle laissé figé : rien à régler ici, l'édition se fait
  // dans le modèle. On le dit plutôt que de présenter un panneau vide.
  if (frozen) {
    return (
      <BoxFormControlsPortal title={t('org_chart')} layout='column'>
        <div style={hintStyle}>{t('org_chart_template_frozen')}</div>
      </BoxFormControlsPortal>
    );
  }

  // Chart choisi : tuiles icône + libellé sur une ligne, comme la section
  // « Actions » de la box (BoxActionsPanel). Sinon : contrôles empilés pleine
  // largeur, le sélecteur a besoin de toute la largeur du panel.
  return (
    <BoxFormControlsPortal title={t('org_chart')} layout={hasChart ? 'row' : 'column'}>
      {hasChart ? (
        <>
          <LabeledControlRow
            label={t('org_chart_edit_short')}
            control={
              <NavbarButton
                tooltipContent={lockedByOther ? `${t('org_chart_locked_by')} ${lockHolder?.name}` : t('org_chart_open_editor')}
                disabled={lockedByOther}
                icon={<FontAwesomeIcon icon={icon({ name: 'pen', style: 'light' })} style={iconButtonStyle()} />}
                onClick={onEdit}
              />
            }
          />
          {isCopy && !lockedByOther && (
            <LabeledControlRow
              label={t('org_chart_sync_short')}
              control={
                <NavbarButton
                  tooltipContent={t('org_chart_sync_button')}
                  icon={<FontAwesomeIcon icon={icon({ name: 'arrows-rotate', style: 'light' })} style={iconButtonStyle()} />}
                  onClick={onSync}
                />
              }
            />
          )}
          {!lockedByOther && (
            <LabeledControlRow
              label={t('org_chart_change_short')}
              control={
                <NavbarButton
                  tooltipContent={t('org_chart_change')}
                  icon={<FontAwesomeIcon icon={icon({ name: 'arrow-right-arrow-left', style: 'light' })} style={iconButtonStyle()} />}
                  onClick={onChangeChart}
                />
              }
            />
          )}
        </>
      ) : (
        <>
          <NavbarSelector
            placeholder={t('org_chart_select_placeholder')}
            // marginRight: 0 — navbarElementStyle réserve une gouttière entre
            // deux contrôles de navbar, inutile ici où le sélecteur est seul sur
            // sa ligne et doit occuper toute la largeur du panel.
            customStyle={{ width: '100%', marginRight: 0 }}
            disabled={isLoading || creatingBlank}
            onSelect={handleSelect}
            options={availableCharts.map((c) => ({
              value: c.id,
              label: multipleEntities ? `${c.entity_name} · ${c.name}` : c.name,
            }))}
          />
          <Button
            size='small'
            disabled={!proposalId || creatingBlank}
            loading={creatingBlank}
            icon={<FontAwesomeIcon icon={icon({ name: 'plus', style: 'light' })} />}
            onClick={() => createBlank()}
            style={{ width: '100%' }}
          >
            {t('org_chart_create_blank')}
          </Button>
        </>
      )}
    </BoxFormControlsPortal>
  );
};

export default OrgChartPanel;
